/**
 * Barre de filtres globaux : Site, Personne, Année, Mois, Trimestre.
 * Tous les widgets du cockpit se mettent à jour automatiquement (via
 * FilterContext) lorsqu'un filtre change.
 */
import { useMemo } from "react";
import { Star } from "lucide-react";
import FilterSelect from "@/components/filters/FilterSelect";
import { Button } from "@/components/ui/button";
import { useFilters } from "@/contexts/FilterContext";
import { toast } from "@/hooks/use-toast";
import { FILTERS } from "@/constants/testIds";

const MONTH_LABELS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export default function GlobalFilters() {
  const { filters, updateFilter, options, optionsLoading, favoriteFilters, saveFavoriteFilters, clearFavoriteFilters } = useFilters();

  const isFavoriteActive = favoriteFilters && ["site", "email", "year", "month", "quarter"].every(
    (key) => (favoriteFilters[key] ?? "all") === filters[key]
  );

  const handleToggleFavorite = async () => {
    if (isFavoriteActive) {
      await clearFavoriteFilters();
      toast({ title: "Filtre favori retiré", description: "Cette combinaison ne sera plus appliquée automatiquement." });
    } else {
      await saveFavoriteFilters();
      toast({
        title: "Filtres favoris enregistrés",
        description: "Cette combinaison sera appliquée automatiquement à votre prochaine connexion.",
      });
    }
  };

  const siteOptions = useMemo(
    () => [{ value: "all", label: "Tous les sites" }, ...options.sites.map((s) => ({ value: s, label: s }))],
    [options.sites]
  );
  const personOptions = useMemo(
    () => [
      { value: "all", label: "Toutes les personnes" },
      ...options.persons.map((p) => ({ value: p.email, label: p.email, searchText: `${p.email} ${p.name}` })),
    ],
    [options.persons]
  );
  const yearOptions = useMemo(
    () => [{ value: "all", label: "Toutes les années" }, ...options.years.map((y) => ({ value: String(y), label: String(y) }))],
    [options.years]
  );
  const monthOptions = useMemo(
    () => [{ value: "all", label: "Tous les mois" }, ...options.months.map((m) => ({ value: String(m), label: MONTH_LABELS[m - 1] }))],
    [options.months]
  );
  const quarterOptions = useMemo(
    () => [{ value: "all", label: "Tous les trimestres" }, ...options.quarters.map((q) => ({ value: String(q), label: `T${q}` }))],
    [options.quarters]
  );

  return (
    <div data-testid={FILTERS.container} className="flex flex-wrap items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 md:px-6">
      <FilterSelect
        testId={FILTERS.siteSelect}
        label="Site"
        value={filters.site}
        onChange={(v) => updateFilter("site", v)}
        options={siteOptions}
        searchPlaceholder="Rechercher un site..."
        loading={optionsLoading}
      />
      <FilterSelect
        testId={FILTERS.personSelect}
        label="Personne"
        value={filters.email}
        onChange={(v) => updateFilter("email", v)}
        options={personOptions}
        searchPlaceholder="Rechercher par email..."
        loading={optionsLoading}
      />
      <FilterSelect
        testId={FILTERS.yearSelect}
        label="Année"
        value={filters.year}
        onChange={(v) => updateFilter("year", v)}
        options={yearOptions}
        loading={optionsLoading}
      />
      <FilterSelect
        testId={FILTERS.monthSelect}
        label="Mois"
        value={filters.month}
        onChange={(v) => updateFilter("month", v)}
        options={monthOptions}
        loading={optionsLoading}
      />
      <FilterSelect
        testId={FILTERS.quarterSelect}
        label="Trimestre"
        value={filters.quarter}
        onChange={(v) => updateFilter("quarter", v)}
        options={quarterOptions}
        loading={optionsLoading}
      />
      <Button
        data-testid={FILTERS.saveFavoriteButton}
        variant="outline"
        size="sm"
        onClick={handleToggleFavorite}
        className={`ml-auto gap-1.5 border-slate-200 text-xs ${isFavoriteActive ? "text-amber-600" : "text-slate-500"}`}
        title={isFavoriteActive ? "Retirer cette combinaison des favoris" : "Enregistrer cette combinaison de filtres comme favorite"}
      >
        <Star className={`h-3.5 w-3.5 ${isFavoriteActive ? "fill-amber-500 text-amber-500" : ""}`} />
        Favori
      </Button>
    </div>
  );
}
